import { FirestoreError } from 'firebase/firestore';
import { logStreamError } from '../utils/globalErrorLogger';

/**
 * Codes meaning the request itself was rejected, not a dropped stream —
 * retrying changes nothing. `subscribeWithRetry` (`./firestore.ts`) checks
 * this to stop re-attaching instead of looping against a permission or query
 * defect forever.
 */
const TERMINAL_FIRESTORE_CODES = new Set([
  'permission-denied',
  'unauthenticated',
  'failed-precondition',
  'invalid-argument',
  'not-found',
]);

/**
 * Codes for a dropped transport stream (WebChannel reset, token refresh
 * race, backend blip) that `subscribeWithRetry`'s backoff recovers from on
 * its own — these log at WARNING rather than ERROR so a self-healing blip
 * doesn't page like a real defect.
 */
const TRANSIENT_FIRESTORE_CODES = new Set([
  'internal',
  'unavailable',
  'cancelled',
  'deadline-exceeded',
  'unknown',
  'aborted',
]);

export function isTerminalFirestoreError(err: FirestoreError): boolean {
  return TERMINAL_FIRESTORE_CODES.has(err.code);
}

/**
 * Standard onError handler for Firestore `onSnapshot` subscriptions. Forwards to
 * Cloud Logging with a context label so a dropped listener is never silent.
 */
export function wrapSubscriptionError(
  context: string,
  onError?: (err: FirestoreError) => void,
): (err: FirestoreError) => void {
  return (err: FirestoreError) => {
    logStreamError(err, {
      context,
      severity: TRANSIENT_FIRESTORE_CODES.has(err.code) ? 'WARNING' : undefined,
    });
    onError?.(err);
  };
}
